import type { MenuProps } from 'antd'
import { Dropdown } from 'antd'
import { ArrowRight, ExternalLink, LineChart, Plus } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const FADE_OUT_DURATION_MS = 1500

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const TriggerAnchor = styled.div<{ $x: number; $y: number; $fading: boolean }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: 1px;
  height: 1px;
  pointer-events: none;

  ${({ $fading }) =>
    $fading &&
    css`
      & + .ant-dropdown {
        animation: ${fadeOut} ${FADE_OUT_DURATION_MS}ms ease-out forwards;
      }
    `}
`

const MenuLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const MenuShortcut = styled.span`
  opacity: 0.6;
  font-size: 0.625rem;
`

export interface DashboardTabInfo {
  id: string
  label: string
}

export type ContextMenuProps = {
  position: { x: number; y: number } | null
  onClose: () => void
  onOpen?: () => void
  onOpenInNewTab?: () => void
  onAddToContext?: () => void
  onAddToExistingDashboard?: (tabId: string) => void
  showNavigationOptions?: boolean
  showAddToContext?: boolean
  existingDashboardTabs?: DashboardTabInfo[]
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  position,
  onClose,
  onOpen,
  onOpenInNewTab,
  onAddToContext,
  onAddToExistingDashboard,
  showNavigationOptions = true,
  showAddToContext = false,
  existingDashboardTabs = [],
}) => {
  const [isFading, setIsFading] = useState(false)

  const handleOpen = useCallback(() => {
    onOpen?.()
    onClose()
  }, [onOpen, onClose])

  const handleOpenInNewTab = useCallback(() => {
    onOpenInNewTab?.()
    onClose()
  }, [onOpenInNewTab, onClose])

  const handleAddToContext = useCallback(() => {
    onAddToContext?.()
    setIsFading(true)
    setTimeout(() => {
      setIsFading(false)
      onClose()
    }, FADE_OUT_DURATION_MS)
  }, [onAddToContext, onClose])

  const handleAddToExistingDashboard = useCallback(
    (tabId: string) => {
      onAddToExistingDashboard?.(tabId)
      setIsFading(true)
      setTimeout(() => {
        setIsFading(false)
        onClose()
      }, FADE_OUT_DURATION_MS)
    },
    [onAddToExistingDashboard, onClose]
  )

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = []

    if (showNavigationOptions && onOpen) {
      items.push({
        key: 'open',
        icon: <ArrowRight size={14} />,
        label: 'Open',
        onClick: handleOpen,
      })
    }

    if (showNavigationOptions && onOpenInNewTab) {
      items.push({
        key: 'open-new-tab',
        icon: <ExternalLink size={14} />,
        label: (
          <MenuLabel>
            Open in New Tab
            <MenuShortcut>⌘+Click</MenuShortcut>
          </MenuLabel>
        ),
        onClick: handleOpenInNewTab,
      })
    }

    if (showAddToContext && onAddToContext) {
      if (items.length > 0) {
        items.push({ type: 'divider' })
      }
      items.push({
        key: 'add-to-context',
        icon: <Plus size={14} />,
        label: 'Add to context',
        onClick: handleAddToContext,
      })
    }

    if (
      showAddToContext &&
      existingDashboardTabs.length > 0 &&
      onAddToExistingDashboard
    ) {
      items.push({
        key: 'add-to-dashboard',
        icon: <LineChart size={14} />,
        label: 'Add to dashboard',
        children: existingDashboardTabs.map((tab) => ({
          key: `dashboard-${tab.id}`,
          label: tab.label,
          onClick: () => handleAddToExistingDashboard(tab.id),
        })),
      })
    }

    return items
  }, [
    showNavigationOptions,
    showAddToContext,
    onOpen,
    onOpenInNewTab,
    onAddToContext,
    onAddToExistingDashboard,
    existingDashboardTabs,
    handleOpen,
    handleOpenInNewTab,
    handleAddToContext,
    handleAddToExistingDashboard,
  ])

  if (!position || menuItems.length === 0) {
    return null
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      open={true}
      onOpenChange={(open) => {
        if (!open && !isFading) {
          onClose()
        }
      }}
      trigger={['click']}>
      <TriggerAnchor $x={position.x} $y={position.y} $fading={isFading} />
    </Dropdown>
  )
}
